import { useState } from 'react';
import { ActivityTyp, ActivityTypConfig, CreateActivityDto } from '../../types/activity';
import { MentionInput } from '../notifications/MentionInput';
import { toLocalDatetimeInput } from '../../utils/datetime';

interface ActivityFormProps {
  initialData?: Partial<CreateActivityDto>;
  onSubmit: (data: CreateActivityDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ActivityForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}: ActivityFormProps) {
  const [typ, setTyp] = useState<ActivityTyp>(initialData?.typ || ActivityTyp.NOTE);
  const [subject, setSubject] = useState(initialData?.subject || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [duration, setDuration] = useState<number | undefined>(initialData?.duration);
  const [occurredAt, setOccurredAt] = useState(
    initialData?.occurredAt
      ? toLocalDatetimeInput(new Date(initialData.occurredAt))
      : toLocalDatetimeInput(new Date()),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      typ,
      subject: subject || undefined,
      content,
      duration: duration || undefined,
      occurredAt: new Date(occurredAt).toISOString(),
    });
  };

  const showDuration = typ === ActivityTyp.CALL || typ === ActivityTyp.MEETING;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Activity Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Aktivitätstyp
        </label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(ActivityTypConfig).map(([key, config]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTyp(key as ActivityTyp)}
              className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                typ === key
                  ? `${config.bgColor} ${config.color} ring-2 ring-offset-2 ring-blue-500`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Subject */}
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
          Betreff (optional)
        </label>
        <input
          type="text"
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          placeholder="z.B. Erstgespräch, Terminvereinbarung..."
        />
      </div>

      {/* Content */}
      <div>
        <label htmlFor="content" className="block text-sm font-medium text-gray-700">
          Inhalt
        </label>
        {typ === ActivityTyp.NOTE ? (
          <MentionInput
            value={content}
            onChange={setContent}
            placeholder="Schreiben Sie eine Notiz... (@ um jemanden zu erwaehnen)"
            rows={4}
            required
            className="mt-1"
          />
        ) : (
          <textarea
            id="content"
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Beschreiben Sie die Aktivitaet..."
          />
        )}
        <p className="mt-1 text-xs text-gray-500">{content.length} Zeichen</p>
      </div>

      {/* Occurred At */}
      <div>
        <label htmlFor="occurredAt" className="block text-sm font-medium text-gray-700">
          Zeitpunkt
        </label>
        <input
          type="datetime-local"
          id="occurredAt"
          value={occurredAt}
          onChange={(e) => setOccurredAt(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        />
      </div>

      {/* Duration (for calls/meetings) */}
      {showDuration && (
        <div>
          <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
            Dauer (Minuten)
          </label>
          <input
            type="number"
            id="duration"
            min={1}
            value={duration || ''}
            onChange={(e) => setDuration(e.target.value ? parseInt(e.target.value) : undefined)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="z.B. 30"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={isLoading}
        >
          Abbrechen
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={isLoading || !content.trim()}
        >
          {isLoading ? 'Speichern...' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}
